import { cn } from "@/lib/utils";
import { useLanguage } from "@/lib/i18n";
import { trackStoreClick } from "@/lib/analytics";
import {
  appStoreAlt,
  appStoreBadge,
  playBadge,
  playStoreAlt,
  useStoreLinks,
} from "@/lib/stores";

/**
 * The two official store badges (Apple App Store + Google Play), side by side,
 * in the page language. Both images are cropped to their visible artwork, so
 * they share one height class and keep their own proportions via `w-auto`.
 *
 * `size`: "sm" = 44px, "md" = 56px badges (default), "lg" = 64px on sm+ screens.
 */
export function StoreBadges({
  location,
  size = "md",
  className,
  appStoreUrl: appStoreOverride,
  playStoreUrl: playStoreOverride,
}: {
  location: string;
  size?: "sm" | "md" | "lg";
  className?: string;
  appStoreUrl?: string;
  playStoreUrl?: string;
}) {
  const { language } = useLanguage();
  const links = useStoreLinks(language);
  const appStoreUrl = appStoreOverride ?? links.appStoreUrl;
  const playStoreUrl = playStoreOverride ?? links.playStoreUrl;

  const badgeH =
    size === "sm" ? "h-11" : size === "lg" ? "h-14 sm:h-16" : "h-14";
  const apple = appStoreBadge(language);
  const play = playBadge(language);

  return (
    <div
      className={cn(
        "flex flex-wrap items-center justify-center gap-3",
        className,
      )}
    >
      <a
        href={appStoreUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackStoreClick("app_store", location, language)}
        className="inline-flex items-center transition-transform duration-200 hover:scale-[1.03] active:scale-95"
        aria-label={appStoreAlt(language)}
      >
        <img
          src={apple.src}
          alt={appStoreAlt(language)}
          width={apple.width}
          height={apple.height}
          className={cn("w-auto", badgeH)}
        />
      </a>
      <a
        href={playStoreUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackStoreClick("google_play", location, language)}
        className="inline-flex items-center transition-transform duration-200 hover:scale-[1.03] active:scale-95"
        aria-label={playStoreAlt(language)}
      >
        <img
          src={play.src}
          alt={playStoreAlt(language)}
          width={play.width}
          height={play.height}
          className={cn("w-auto", badgeH)}
        />
      </a>
    </div>
  );
}

/** Google Play's official triangle glyph, for compact text buttons. */
/** The real Apple logo (official glyph), not a generic apple icon. */
export function AppleLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 384 512" aria-hidden="true" fill="currentColor" className={className}>
      <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.6-2.8-74.3 20.7-88.5 20.7-14.9 0-49.7-20.1-77.1-20.1C61.1 141.6 0 184.6 0 269.5c0 25.1 4.6 51 13.8 77.5 12.3 34.9 56.6 120.5 102.8 119.1 24.2-.6 41.3-17.2 72.8-17.2 30.6 0 46.4 17.2 73.5 17.2 46.6-.7 86.7-78.4 98.4-113.4-62.6-29.5-59.3-86.4-59.3-83zM256.7 76.6c25.3-30 23-57.3 22.3-67.1-22.3 1.3-48.1 15.2-62.8 32.3-16.2 18.3-25.7 40.9-23.7 65.9 24.1 1.9 46.1-10.5 64.2-31.1z" />
    </svg>
  );
}

export function GooglePlayLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 512 512" aria-hidden="true" fill="currentColor" className={className}>
      <path d="M48 59.5v393c0 7.5 4 14 10.5 17.6L279 256 58.5 41.9C52 45.5 48 52 48 59.5zM352.6 329.6 103.4 473.2l192.9-193.2 56.3 49.6zM399.5 208.3c7.4 5.6 12.5 12.1 12.5 20.4v54.6c0 8.3-5.1 14.8-12.5 20.4l-42.4 24.6-62-61.9 62-62 42.4 23.9zM103.4 38.8l249.2 143.6-56.3 49.6L103.4 38.8z" />
    </svg>
  );
}
